import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  type DialogShellFooterProps
} from '@bliks-io/ui';
import { CrudErrorAlert } from '@/components/crud';

interface ReviewModalLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: LucideIcon;
  title: string;
  description: string;
  error?: string;
  onDismissError?: () => void;
  children: ReactNode;
  /**
   * Footer action slots forwarded to `DialogShellFooter`. When omitted, no
   * footer is rendered.
   */
  footer?: DialogShellFooterProps;
}

/**
 * Shared layout for review modals. Renders the `DialogShell` composition
 * with a standardized header, scrollable body, and slot-based footer.
 */
export function ReviewModalLayout({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  error,
  onDismissError,
  children,
  footer
}: ReviewModalLayoutProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="lg">
        <DialogShellHeader
          icon={<Icon className="h-5 w-5" />}
          title={title}
          subtitle={description}
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody>
          {error && <CrudErrorAlert error={error} onDismiss={onDismissError} />}
          {children}
        </DialogShellBody>
        {footer && <DialogShellFooter {...footer} />}
      </DialogShell>
    </Dialog>
  );
}
